import React, { memo } from "react";

import getStrainTags from "@/api/requests/getStrainTags";
import {
  STRAIN_MEDICAL_DISCLAIMER_CA,
  STRAIN_MEDICAL_DISCLAIMER_US,
} from "@/constants/disclaimers";
import { AddToBagProvider } from "@/context/AddToBagContext";
import { AdMarqueePageLocation } from "@/custom-types/AdMarquee";
import { ReportedOptions } from "@/custom-types/StrainList";
import useUserFavorites from "@/hooks/useUserFavorites";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";

import AdMarqueeCarousel from "@/components/AdMarqueeCarousel";
import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import ExploreOtherCategories from "@/components/ExploreOtherCategories";
import GoogleAd from "@/components/GoogleAd";
import HighThcStrains from "@/components/HighThcStrains";
import ListCampaignSection from "@/components/ListCampaignSection";
import MedicalDisclaimers from "@/components/MedicalDisclaimers";
import MetaTags from "@/components/MetaTags";
import PopularStrainsSection from "@/components/PopularStrainsSection";
import StrainEffectTiles from "@/components/StrainEffectTiles";
import StrainTypeTiles from "@/components/StrainTypeTiles";
import TileSections from "@/components/TileSections";

type StrainListsPageProps = {
  reportedOptions: ReportedOptions;
};

const StrainListsPage: React.FC<StrainListsPageProps> = ({
  reportedOptions,
}) => {
  const { useInitiateFavorites } = useUserFavorites();

  useInitiateFavorites();

  const { categories, effects } = reportedOptions;
  return (
    <>
      <MetaTags
        title="Explore the Best Weed Strains: Indica, Sativa & Hybrid | Leafly"
        description="Explore Leafly's curated lists of weed strains to help you choose the best strain by indica, sativa or hybrid as well as desired effect & popularity."
        image="https://public.leafly.com/web-strain-explorer/explore-og-image.png"
      />
      <AddToBagProvider>
        <div className="container">
          <h1 className="pt-8 mb-xl">Find the perfect strain for you.</h1>
          <AdMarqueeCarousel
            pageType={AdMarqueePageLocation.STRAINS_LIST_PAGE}
          />

          <PopularStrainsSection />
          <div className="gam-rectangular-fixed-height">
            <GoogleAd
              adPath="/13212770/strain/explore"
              targeting={{ playlist: "Explore Dynamic" }}
              sizes={{ desktop: [728, 90], mobile: [320, 50] }}
            />
          </div>
          <hr className="border-light-grey w-full mt-xxl" />
          <ListCampaignSection />
          {categories?.length && <StrainTypeTiles categories={categories} />}
          <div className="full-bleed-container py-xl bg-leafly-white">
            <HighThcStrains />
          </div>
          {effects?.length && <StrainEffectTiles effects={effects} />}
          <TileSections />
          <hr className="border-light-grey w-full" />
          <ExploreOtherCategories
            reportedOptions={reportedOptions}
            className="pt-xxl mb-xl"
          />
          <MedicalDisclaimers
            usDisclaimer={STRAIN_MEDICAL_DISCLAIMER_US}
            caDisclaimer={STRAIN_MEDICAL_DISCLAIMER_CA}
            className="mb-xl"
          />
          <section className="mb-xxl">
            <h2 className="mb-md text-md">Explore cannabis strains</h2>
            <p>
              Marijuana strains come in different aromas, flavors and effects.
              Use Leafly’s strain explorer to discover the best strains for you
              based on your desired experience.
            </p>
          </section>
        </div>
        <Breadcrumbs>
          <Breadcrumb href="/strains/lists" currentLocation>
            Strain lists
          </Breadcrumb>
        </Breadcrumbs>
      </AddToBagProvider>
    </>
  );
};

export const getServerSideProps =
  createGetServerSideProps<StrainListsPageProps>(async () => {
    const tags = await getStrainTags();

    const reportedOptions = {
      categories: tags.categories,
      conditions: tags.conditions.concat(tags.symptoms),
      effects: tags.effects,
      flavors: tags.flavors,
      terpenes: tags.terpenes,
    };

    return {
      props: {
        reportedOptions,
      },
    };
  });

export default memo(StrainListsPage);
